import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { contentAPI } from '../services/api';
import useNavigationWarning from '../hooks/useNavigationWarning';
import { checkAuthBeforeAction } from '../utils/authHelper';
import NavigationWarningModal from '../components/NavigationWarningModal';
import usePermission from '../hooks/usePermission';
import UpgradePrompt from '../components/UpgradePrompt';

const ImageGeneration = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Permission check
  const { hasFeatureAccess, getPlanInfo, getRequiredPlanForFeature } = usePermission();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradePromptData, setUpgradePromptData] = useState({});
  const [mode, setMode] = useState('text-to-image'); // 'text-to-image' or 'image-to-image'
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [imageSize, setImageSize] = useState('1024x1024');
  const [imageStyle, setImageStyle] = useState('vivid');
  const [numImages, setNumImages] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [optimizingPrompt, setOptimizingPrompt] = useState(false);
  const [sourceImage, setSourceImage] = useState(null);
  const [sourceImagePreview, setSourceImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const navigationWarning = useNavigationWarning(generating || optimizingPrompt, 'An image is being generated. If you leave now, the generation will continue in the background. Are you sure you want to leave?', 'image-generation');

  // Load prefill data from Workspace if available
  useEffect(() => {
    if (location.state?.prefillData) {
      const {
        prompt: prefillPrompt,
        negativePrompt: prefillNegativePrompt,
        style,
        size,
        previousImage
      } = location.state.prefillData;

      if (prefillPrompt) {
        setPrompt(prefillPrompt);
      }

      if (prefillNegativePrompt) {
        setNegativePrompt(prefillNegativePrompt);
      }

      if (style) {
        setImageStyle(style);
      }

      if (size) {
        setImageSize(size);
      }

      if (previousImage) {
        setGeneratedImages([{
          imageUrl: previousImage,
          prompt: prefillPrompt || '',
          style: style || 'vivid',
          size: size || '1024x1024'
        }]);
        setSelectedImage(0);
      }

      // Clear the navigation state to prevent re-filling on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const imageSizes = [
    { value: '1024x1024', label: 'Square', subtitle: '1024×1024', icon: '⬜' },
    { value: '1792x1024', label: 'Landscape', subtitle: '1792×1024', icon: '▭' },
    { value: '1024x1792', label: 'Portrait', subtitle: '1024×1792', icon: '▯' }
  ];

  const imageStyles = [
    { value: 'vivid', label: 'Vivid', description: 'Hyper-realistic and dramatic' },
    { value: 'natural', label: 'Natural', description: 'More natural, less hyper-real' },
    { value: 'anime', label: 'Anime', description: 'Anime and manga style' },
    { value: 'digital-art', label: 'Digital Art', description: 'Digital painting style' },
    { value: 'photographic', label: 'Photographic', description: 'Realistic photography' },
    { value: '3d-model', label: '3D Model', description: '3D rendered style' }
  ];

  const promptSuggestions = [
    'A serene landscape with mountains and a lake at sunset',
    'A futuristic cityscape with flying cars and neon lights',
    'A cozy coffee shop interior with warm lighting',
    'A mystical forest with glowing mushrooms and fireflies',
    'An astronaut exploring an alien planet with two moons',
    'A steampunk-inspired mechanical dragon'
  ];

  const handleGenerate = async () => {
    // Check authentication before making API call
    if (!checkAuthBeforeAction(navigate)) {
      return;
    }

    if (!prompt.trim()) {
      setError('Please enter a description for your image');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (mode === 'image-to-image' && !sourceImage) {
      setError('Please upload a source image for Image to Image mode');
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Check feature permission
    if (!hasFeatureAccess('ai-image-generation')) {
      const planInfo = getPlanInfo();
      setUpgradePromptData({
        type: 'feature',
        featureName: 'AI Image Generation',
        currentPlan: planInfo.name,
        requiredPlan: getRequiredPlanForFeature('ai-image-generation')
      });
      setShowUpgradePrompt(true);
      return;
    }

    setGenerating(true);
    setError('');
    setGeneratedImages([]);
    setSelectedImage(null);

    try {
      let response;

      if (mode === 'image-to-image') {
        // For image-to-image, send as FormData
        const formData = new FormData();
        formData.append('prompt', prompt.trim());
        if (negativePrompt.trim()) {
          formData.append('negativePrompt', negativePrompt.trim());
        }
        formData.append('size', imageSize);
        formData.append('style', imageStyle);
        formData.append('n', numImages);
        formData.append('sourceImage', sourceImage);

        response = await contentAPI.generateImageToImage(formData);
      } else {
        // For text-to-image, send as JSON
        response = await contentAPI.generateImage({
          prompt: prompt.trim(),
          negativePrompt: negativePrompt.trim() || undefined,
          size: imageSize,
          style: imageStyle,
          n: numImages
        });
      }

      if (response.data.success) {
        setGeneratedImages(response.data.data.images);
        setSelectedImage(0);
      } else {
        setError(response.data.error?.message || 'Failed to generate image');
      }
    } catch (err) {
      console.error('Image generation error:', err);
      setError(err.response?.data?.error?.message || 'Failed to generate image. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = (imageUrl, index) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `ai-generated-image-${index + 1}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePromptSuggestion = (suggestion) => {
    setPrompt(suggestion);
  };

  const handleClear = () => {
    setPrompt('');
    setNegativePrompt('');
    setGeneratedImages([]);
    setSelectedImage(null);
    setError('');
  };

  const handleOptimizePrompt = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt first');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setOptimizingPrompt(true);
    setError('');

    try {
      const response = await contentAPI.optimizeImagePrompt({
        prompt: prompt.trim(),
        style: imageStyle,
        mode: mode // Pass the current mode (text-to-image or image-to-image)
      });

      if (response.data.success) {
        setPrompt(response.data.data.optimizedPrompt);
      } else {
        setError(response.data.error?.message || 'Failed to optimize prompt');
        setTimeout(() => setError(''), 3000);
      }
    } catch (err) {
      console.error('Prompt optimization error:', err);
      setError(err.response?.data?.error?.message || 'Failed to optimize prompt. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setOptimizingPrompt(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload a valid image file');
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setUploadingImage(true);
    setSourceImage(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setSourceImagePreview(reader.result);
      setUploadingImage(false);
    };
    reader.onerror = () => {
      setError('Failed to read image file');
      setTimeout(() => setError(''), 3000);
      setUploadingImage(false);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveSourceImage = () => {
    setSourceImage(null);
    setSourceImagePreview(null);
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    // Clear source image when switching to text-to-image mode
    if (newMode === 'text-to-image') {
      setSourceImage(null);
      setSourceImagePreview(null);
    }
  };

  return (
    <>
      <NavigationWarningModal
        isOpen={navigationWarning.showModal}
        onCancel={navigationWarning.handleCancelLeave}
        onConfirm={navigationWarning.handleConfirmLeave}
        message={navigationWarning.warningMessage}
        taskType={navigationWarning.taskType}
      />

      <UpgradePrompt
        show={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        type={upgradePromptData.type}
        featureName={upgradePromptData.featureName}
        currentPlan={upgradePromptData.currentPlan}
        requiredPlan={upgradePromptData.requiredPlan}
      />

      <div className="flex h-screen bg-gray-50">
        <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    AI Image Generation
                  </h1>
                  <p className="text-gray-600 mt-1">
                    Create stunning images from text descriptions using advanced AI
                  </p>
                </div>
              </div>
            </div>

            {/* Mode Selection */}
            <div className="mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 inline-flex gap-2">
                <button
                  onClick={() => handleModeChange('text-to-image')}
                  className={`px-6 py-3 rounded-lg font-medium transition-all ${
                    mode === 'text-to-image'
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Text to Image</span>
                  </div>
                </button>
                <button
                  onClick={() => handleModeChange('image-to-image')}
                  className={`px-6 py-3 rounded-lg font-medium transition-all ${
                    mode === 'image-to-image'
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Image to Image</span>
                  </div>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Panel - Input & Settings */}
              <div className="lg:col-span-1 space-y-6">
                {/* Source Image Upload - Only for Image to Image mode */}
                {mode === 'image-to-image' && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Source Image
                    </h2>

                    {!sourceImagePreview ? (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-purple-500 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          id="source-image-upload"
                          disabled={uploadingImage}
                        />
                        <label
                          htmlFor="source-image-upload"
                          className="cursor-pointer"
                        >
                          <div className="flex flex-col items-center">
                            <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            <p className="text-sm font-medium text-gray-900 mb-1">
                              {uploadingImage ? 'Uploading...' : 'Click to upload image'}
                            </p>
                            <p className="text-xs text-gray-500">
                              PNG, JPG, WEBP (max 10MB)
                            </p>
                          </div>
                        </label>
                      </div>
                    ) : (
                      <div className="relative">
                        <img
                          src={sourceImagePreview}
                          alt="Source"
                          className="w-full rounded-lg"
                        />
                        <button
                          onClick={handleRemoveSourceImage}
                          className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors shadow-lg"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                        <div className="mt-3">
                          <p className="text-xs text-gray-500 text-center">
                            Source image uploaded successfully
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Prompt Input */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Description
                  </h2>

                  <div className="space-y-4">
                    {/* Main Prompt */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        What do you want to create?
                      </label>
                      <textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        rows="5"
                        placeholder="Describe your image in detail... (e.g., 'A majestic mountain landscape at sunset with vibrant orange and purple skies')"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-sm"
                      />
                      <div className="flex justify-between items-center mt-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">
                            {prompt.length} characters
                          </span>
                          {prompt.trim() && (
                            <button
                              onClick={handleOptimizePrompt}
                              disabled={optimizingPrompt}
                              className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium rounded-full hover:from-purple-600 hover:to-pink-600 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {optimizingPrompt ? (
                                <>
                                  <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  <span>Optimizing...</span>
                                </>
                              ) : (
                                <>
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                  </svg>
                                  <span>AI Optimize</span>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                        {prompt && (
                          <button
                            onClick={() => setPrompt('')}
                            className="text-xs text-gray-500 hover:text-gray-700 flex items-center"
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Clear
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Negative Prompt */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                        Negative Prompt
                        <span className="ml-2 text-xs text-gray-500 font-normal">(Optional)</span>
                      </label>
                      <textarea
                        value={negativePrompt}
                        onChange={(e) => setNegativePrompt(e.target.value)}
                        rows="3"
                        placeholder="What to avoid in the image... (e.g., 'blurry, low quality, distorted')"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-sm"
                      />
                    </div>

                    {/* Prompt Suggestions */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quick Ideas
                      </label>
                      <div className="space-y-2">
                        {promptSuggestions.slice(0, 3).map((suggestion, index) => (
                          <button
                            key={index}
                            onClick={() => handlePromptSuggestion(suggestion)}
                            className="w-full text-left px-3 py-2 text-xs bg-gray-50 hover:bg-purple-50 text-gray-700 hover:text-purple-700 rounded-lg border border-gray-200 hover:border-purple-300 transition-colors"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Settings */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    Settings
                  </h2>

                  <div className="space-y-5">
                    {/* Image Size */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Image Size
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {imageSizes.map((size) => (
                          <button
                            key={size.value}
                            onClick={() => setImageSize(size.value)}
                            className={`p-3 border-2 rounded-lg text-center transition-all ${
                              imageSize === size.value
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-purple-300 bg-white'
                            }`}
                          >
                            <div className="text-2xl mb-1">{size.icon}</div>
                            <div className="text-xs font-medium text-gray-900">{size.label}</div>
                            <div className="text-xs text-gray-500">{size.subtitle}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Image Style */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Style
                      </label>
                      <div className="space-y-2">
                        {imageStyles.map((style) => (
                          <button
                            key={style.value}
                            onClick={() => setImageStyle(style.value)}
                            className={`w-full p-3 border-2 rounded-lg text-left transition-all ${
                              imageStyle === style.value
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-purple-300 bg-white'
                            }`}
                          >
                            <div className="font-medium text-sm text-gray-900">{style.label}</div>
                            <div className="text-xs text-gray-500 mt-1">{style.description}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Number of Images - Hidden, fixed at 1 */}
                    {/* <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Number of Images: {numImages}
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="4"
                        value={numImages}
                        onChange={(e) => setNumImages(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>1</span>
                        <span>2</span>
                        <span>3</span>
                        <span>4</span>
                      </div>
                    </div> */}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={handleGenerate}
                    disabled={generating || !prompt.trim()}
                    className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all shadow-lg ${
                      generating || !prompt.trim()
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transform hover:scale-105'
                    }`}
                  >
                    {generating ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generating...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Generate Image
                      </span>
                    )}
                  </button>

                  {(generatedImages.length > 0 || prompt) && (
                    <button
                      onClick={handleClear}
                      className="w-full py-3 px-6 border-2 border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>

              {/* Right Panel - Generated Images */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-[800px]">
                  <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Generated Images
                    {generatedImages.length > 0 && (
                      <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                        {generatedImages.length}
                      </span>
                    )}
                  </h2>

                  {/* Error Message */}
                  {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex">
                        <svg className="h-5 w-5 text-red-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <div className="ml-3">
                          <p className="text-sm text-red-800">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Image Display */}
                  {generatedImages.length > 0 ? (
                    <div className="space-y-6">
                      {/* Main Image Display */}
                      {selectedImage !== null && generatedImages[selectedImage] && (
                        <div className="space-y-4">
                          <div className="relative group bg-gray-100 rounded-xl overflow-hidden">
                            <img
                              src={generatedImages[selectedImage].imageUrl}
                              alt={`Generated image ${selectedImage + 1}`}
                              className="w-full h-auto"
                            />

                            {/* Image Overlay Actions */}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <button
                                onClick={() => handleDownload(generatedImages[selectedImage].imageUrl, selectedImage)}
                                className="mx-2 px-6 py-3 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition-colors shadow-lg transform hover:scale-105"
                              >
                                <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Download
                              </button>
                            </div>
                          </div>

                          {/* Image Info */}
                          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <h3 className="text-sm font-semibold text-gray-900 mb-2">Prompt</h3>
                            <p className="text-sm text-gray-700 mb-3">
                              {generatedImages[selectedImage].prompt || prompt}
                            </p>
                            <div className="flex flex-wrap gap-2 text-xs">
                              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
                                {generatedImages[selectedImage].style || imageStyle}
                              </span>
                              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                                {generatedImages[selectedImage].size || imageSize}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Thumbnail Grid (if multiple images) */}
                      {generatedImages.length > 1 && (
                        <div>
                          <h3 className="text-sm font-medium text-gray-700 mb-3">
                            All Generated Images
                          </h3>
                          <div className="grid grid-cols-4 gap-3">
                            {generatedImages.map((image, index) => (
                              <button
                                key={index}
                                onClick={() => setSelectedImage(index)}
                                className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                                  selectedImage === index
                                    ? 'border-purple-500 shadow-lg scale-105'
                                    : 'border-gray-200 hover:border-purple-300'
                                }`}
                              >
                                <img
                                  src={image.imageUrl}
                                  alt={`Thumbnail ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                {selectedImage === index && (
                                  <div className="absolute inset-0 bg-purple-500 bg-opacity-20 flex items-center justify-center">
                                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                      <div className="w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mb-6">
                        <svg className="w-12 h-12 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-500 mb-2">No images generated yet</h3>
                      <p className="text-sm text-gray-400 text-center max-w-sm">
                        Enter a description and click "Generate Image" to create stunning AI-powered artwork
                      </p>

                      {/* Tips */}
                      <div className="mt-8 w-full max-w-md">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Tips for best results:</h4>
                        <ul className="space-y-2 text-sm text-gray-600">
                          <li className="flex items-start">
                            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Be specific and detailed in your descriptions
                          </li>
                          <li className="flex items-start">
                            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Include lighting, mood, and style preferences
                          </li>
                          <li className="flex items-start">
                            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Use negative prompts to avoid unwanted elements
                          </li>
                          <li className="flex items-start">
                            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Try different styles to find your perfect look
                          </li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default ImageGeneration;
